import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { debounce } from "lodash"
import { useAuth, APIError } from "../../../../../app/modules/auth"
import {
    EventCheckInRecord,
    getAllEventCheckInLeads,
} from "../../../../../mogul/core/_requests"
import { formatLeadItemDateBetter } from "../DebugPipedrive"
import { SyncLeadButton, useLeadDetail } from "../DebugAllLeads"
import { Link } from "react-router-dom"
import LocationsNeedingAttentionBanner from "../../../../../mogul/components/LocationsNeedingAttentionBanner"
import OriginalDataObjectTableHeader from "./original-data-object-header/OriginalDataObjectTableHeader"
import { OriginalDataObjectFilterObject } from "./original-data-object-header/OriginalDataObjectFilterList"

const checkInsSaveFilterToLocalStorageKey = 'event_check_in_records_table_filter_v1'

export function EventCheckInsTable() {
    const { currentUser } = useAuth();

    const {
        accounts
    } = useLeadDetail()

    // isAdmin is available but not used in this component
    // const isAdmin = !!currentUser?.is_admin;

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const [results, setResults] = useState<EventCheckInRecord[]>([]);
    const [totalResults, setTotalResults] = useState(0);

    const [currentFilterObject, setCurrentFilterObject] = useState<OriginalDataObjectFilterObject | null>(null);
    const [personSearchFilter, setPersonSearchFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const isFirstRender = useRef(true);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const loadList = useCallback(
        async (forceFilter?: OriginalDataObjectFilterObject, page: number = 1) => {
            setErrorLoading(null);
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            try {
                const {
                    event_check_in_records: result,
                    total_event_check_in_records: totalResult,
                } = await getAllEventCheckInLeads({
                    limit: 25, // the backend does quite a bit of computation so we limit to 25
                    page,
                    filter: forceFilter
                        ? {
                            location_unique_ids: forceFilter?.selectedLocationUniqueIds || [],
                            only_unsynced_with_360: forceFilter?.onlyUnSyncedWith360 || false,
                            start_unix_ts: forceFilter?.startingUnixTimestamp || undefined,
                            end_unix_ts: forceFilter?.endingUnixTimestamp || undefined,
                            person_search_filter: personSearchFilter || undefined,
                        }
                        : {
                            location_unique_ids: currentFilterObject?.selectedLocationUniqueIds || [],
                            only_unsynced_with_360: currentFilterObject?.onlyUnSyncedWith360 || false,
                            start_unix_ts: currentFilterObject?.startingUnixTimestamp || undefined,
                            end_unix_ts: currentFilterObject?.endingUnixTimestamp || undefined,
                            person_search_filter: personSearchFilter || undefined,
                        },
                });
                if (page === 1) {
                    setResults(result);
                } else {
                    setResults((prevResults) => [...prevResults, ...result]);
                }
                setTotalResults(totalResult);
                setHasMore(result.length === 25); // Assume there's more if we received a full page of results
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError ? exception.message : 'Failed to load leads';
                setErrorLoading(errorMessage);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [currentFilterObject?.selectedLocationUniqueIds, currentFilterObject?.onlyUnSyncedWith360, currentFilterObject?.startingUnixTimestamp, currentFilterObject?.endingUnixTimestamp, personSearchFilter]
    );

    useEffect(() => {
        // Load from local storage
        try {
            const savedFilter = localStorage.getItem(checkInsSaveFilterToLocalStorageKey);
            if (savedFilter) {
                const parsedFilter = JSON.parse(savedFilter);
                setCurrentFilterObject(parsedFilter);
                loadList(parsedFilter);
            } else {
                loadList();
            }
        } catch (error) {
            console.error('Failed to load saved filter', error);
        }
        // only load once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMore = () => {
        if (hasMore && !loading) {
            setCurrentPage((prevPage) => prevPage + 1);
            loadList(currentFilterObject || undefined, currentPage + 1);
        }
    };

    // Create debounced search handler
    const debouncedSearch = useMemo(
        () => debounce((searchValue: string) => {
            setPersonSearchFilter(searchValue);
        }, 300),
        []
    );

    // Handle search input change
    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    // Load list when search filter changes
    useEffect(() => {
        if (!isFirstRender.current) {
            setCurrentPage(1);
            loadList(currentFilterObject || undefined, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personSearchFilter]);

    // Mark first render as complete
    useEffect(() => {
        isFirstRender.current = false;
    }, []);

    if (loading) {
        return <div className='p-10'>
            <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
            </div>
        </div>
    }

    if (errorLoading) {
        return <div className='p-10'>
            <div className="alert alert-danger" role="alert">
                {errorLoading}
            </div>
            <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={() => {
                    loadList()
                }}>
                    Retry
                </button>
            </div>
        </div>
    }

    return <div>
        {false && <LocationsNeedingAttentionBanner inCategory="event-checkins" />}
        <OriginalDataObjectTableHeader
            typeOfLead="event_check_in"
            title={totalResults === 1 ? '1 Event Check-In' : `${totalResults} Event Check-Ins`}
            filterLocalStorageKey={checkInsSaveFilterToLocalStorageKey}
            currentFilter={currentFilterObject}
            onApplyFilter={(filterObject) => {
                setCurrentFilterObject(filterObject)
                loadList(filterObject)
            }}
            personSearchFilter={personSearchFilter}
            searchBar={
                <div className='d-flex align-items-center position-relative'>
                    <span className='svg-icon svg-icon-1 position-absolute ms-6'>
                        <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                            <path d='M21.7 18.9L18.6 15.8C17.9 16.9 16.9 17.9 15.8 18.6L18.9 21.7C19.3 22.1 19.9 22.1 20.3 21.7L21.7 20.3C22.1 19.9 22.1 19.3 21.7 18.9Z' fill='currentColor' />
                            <path opacity='0.3' d='M11 20C6 20 2 16 2 11C2 6 6 2 11 2C16 2 20 6 20 11C20 16 16 20 11 20ZM11 4C7.1 4 4 7.1 4 11C4 14.9 7.1 18 11 18C14.9 18 18 14.9 18 11C18 7.1 14.9 4 11 4ZM8 11C8 9.3 9.3 8 11 8C11.6 8 12 7.6 12 7C12 6.4 11.6 6 11 6C8.2 6 6 8.2 6 11C6 11.6 6.4 12 7 12C7.6 12 8 11.6 8 11Z' fill='currentColor' />
                        </svg>
                    </span>
                    <input
                        type='text'
                        className='form-control form-control-solid w-300px ps-15'
                        placeholder='Search by name, phone, or email...'
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {searchInput && (
                        <button
                            className='btn btn-flush btn-active-color-primary position-absolute end-0'
                            onClick={() => handleSearchChange('')}
                            style={{ right: '10px' }}
                        >
                            <span className='svg-icon svg-icon-2'>
                                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                                    <path opacity='0.3' d='M6 19.7C5.7 19.7 5.5 19.6 5.3 19.4C4.9 19 4.9 18.4 5.3 18L18 5.3C18.4 4.9 19 4.9 19.4 5.3C19.8 5.7 19.8 6.29999 19.4 6.69999L6.7 19.4C6.5 19.6 6.3 19.7 6 19.7Z' fill='currentColor' />
                                    <path d='M18.8 19.7C18.5 19.7 18.3 19.6 18.1 19.4L5.40001 6.69999C5.00001 6.29999 5.00001 5.7 5.40001 5.3C5.80001 4.9 6.40001 4.9 6.80001 5.3L19.5 18C19.9 18.4 19.9 19 19.5 19.4C19.3 19.6 19 19.7 18.8 19.7Z' fill='currentColor' />
                                </svg>
                            </span>
                        </button>
                    )}
                </div>
            }
        />
        <div className='px-10'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                {/* begin::Table head */}
                <thead>
                    <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>Created</th>
                        <th className='min-w-150px'>
                            Funeral Home
                        </th>
                        <th className='min-w-250px'>
                            Deceased
                        </th>
                        <th className='min-w-150px'>
                            Lead Name
                        </th>
                        <th className='min-w-150px'></th>
                    </tr>
                </thead>
                {/* end::Table head */}
                {/* begin::Table body */}
                <tbody>
                    {results.map((eventCheckInRecord: EventCheckInRecord) => {

                        let linkOutToFRMWithURL;
                        try {
                            if (eventCheckInRecord.public_data.latest_highlevel_contact_id && eventCheckInRecord.location?.private_data?.linked_to_frm_location_id) {
                                const matchingAccount = accounts.filter(x => x.unique_identifier === eventCheckInRecord.location.account.unique_identifier); // [0]?.external_ghl_site_url
                                const linkOutBase = matchingAccount.length ? matchingAccount[0].external_ghl_site_url : null;
                                if (linkOutBase) {
                                    linkOutToFRMWithURL = `${linkOutBase}/v2/location/${eventCheckInRecord.location.private_data.linked_to_frm_location_id}/contacts/detail/${eventCheckInRecord.public_data.latest_highlevel_contact_id}`
                                }
                            }
                        } catch (e) {
                            // This is all a little temporary and there's lots of unverified TypeScript stuff here, so just swallowing any possible errors for now.
                        }

                        return <tr key={`item-${eventCheckInRecord.unique_identifier}`} data-unique-identifier={`${eventCheckInRecord.unique_identifier}`}>
                            <td>
                                {formatLeadItemDateBetter(eventCheckInRecord.inserted_at)}
                                {false && 'show identifiers' && <>
                                    <div>
                                        <strong>{eventCheckInRecord.unique_identifier}</strong>
                                    </div>
                                </>}
                            </td>
                            <td>
                                <Link to={`/service-locations/${eventCheckInRecord.location.unique_identifier}`}>
                                    {eventCheckInRecord.location.name || 'Unknown'}
                                </Link>
                                {!eventCheckInRecord.location.private_data?.linked_to_frm_location_id ? <span className='ms-1'>{`(!)`}</span> : <></>}
                            </td>
                            <td>
                                <div>
                                    {eventCheckInRecord.event.event_name || 'Unknown'}
                                    {personSearchFilter && eventCheckInRecord.event.event_name?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                        <span className='badge badge-light-info ms-2'>Deceased Match</span>
                                    )}
                                </div>
                                {eventCheckInRecord.event.public_data?.additional_event_metadata && <div>
                                    Language: {eventCheckInRecord.event.public_data.additional_event_metadata.language || 'Unknown'}
                                </div>}
                            </td>
                            <td>
                                <div>
                                    <span>
                                        {eventCheckInRecord.visitor_full_name || 'Unknown'}
                                    </span>
                                    {eventCheckInRecord.public_data?.visitor_relationship_to_deceased && (
                                        <span className='ms-1'>
                                            {`(${eventCheckInRecord.public_data.visitor_relationship_to_deceased})`}
                                        </span>
                                    )}
                                    {personSearchFilter && eventCheckInRecord.visitor_full_name?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                        <span className='badge badge-light-primary ms-2'>Lead Match</span>
                                    )}
                                </div>
                                {eventCheckInRecord.visitor_phone_number && (
                                    <div>
                                        {eventCheckInRecord.visitor_phone_number}
                                        {personSearchFilter && eventCheckInRecord.visitor_phone_number?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                            <span className='badge badge-light-success ms-2'>Phone Match</span>
                                        )}
                                    </div>
                                )}
                                {eventCheckInRecord.public_data?.visitor_email_address && (
                                    <div>
                                        {eventCheckInRecord.public_data.visitor_email_address}
                                        {personSearchFilter && eventCheckInRecord.public_data.visitor_email_address?.toLowerCase().includes(personSearchFilter.toLowerCase()) && (
                                            <span className='badge badge-light-warning ms-2'>Email Match</span>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td>
                                {linkOutToFRMWithURL ? <div>
                                    <div>
                                        <a href={linkOutToFRMWithURL} target='_blank' rel='noreferrer'>
                                            View in FRM
                                        </a>
                                    </div>
                                    <div className='mt-2'>
                                        <SyncLeadButton uniqueIdentifier={eventCheckInRecord.unique_identifier} isSecondary />
                                    </div>
                                </div> : <>
                                    <SyncLeadButton uniqueIdentifier={eventCheckInRecord.unique_identifier} />
                                </>}
                            </td>
                        </tr>
                    })}
                    {!loading && hasMore && (
                        <tr>
                            <td colSpan={8}>
                                <button
                                    disabled={loadingMore}
                                    onClick={loadMore}
                                    className='btn btn-primary'
                                >
                                    {loadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
                {/* end::Table body */}
            </table>
        </div>
    </div>

}